import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import { useHistory } from 'react-router';

import { Button, FormControl, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useListAgencies } from '~/hooks/apis/agent/useListAgencies';
import useQuery from '~/hooks/useQuery';

import AgencyDetailsModal from './AgencyDetails/AgencyDetailsModal';

const DEFAULT_SIZE_PER_PAGE = 10;

export default function AgencyList() {
  const queryFilters = useQuery();

  const { push: setQueryString } = useHistory();

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const filter = useMemo(() => {
    return {
      page: parseInt(queryFilters.get('page') ?? '0'),
      search: queryFilters.get('search') ?? '',
      id: queryFilters.get('id') ?? null,
    };
  }, [queryFilters]);

  const {
    data: { rows: result = [], total = 0 } = {},
    isFetching,
    refetch: refetchAgencies,
  } = useListAgencies({
    page: filter.page.toString(),
    pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
    ...(filter.search ? { search: filter.search } : {}),
  });

  const onPageChange = useCallback(
    (page: number) => {
      queryFilters.set('page', page.toString());
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onSearchChange = useCallback(
    (search: string) => {
      queryFilters.set('page', '0');
      if (search === '') {
        queryFilters.delete('search');
      } else {
        queryFilters.set('search', search);
      }
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const debouncedOnSearchChange = useMemo(
    () =>
      debounce((search: string) => {
        onSearchChange(search);
      }, 500),
    [onSearchChange],
  );

  const openDetailsModal = useCallback(
    (id: string) => {
      queryFilters.set('id', id);
      setQueryString(`agencies?${queryFilters.toString()}`, { replace: true });
      setIsDetailsModalOpen(true);
    },
    [setIsDetailsModalOpen, setQueryString, queryFilters],
  );

  const closeDetailsModal = useCallback(() => {
    queryFilters.delete('id');
    setQueryString(`?${queryFilters.toString()}`, { replace: true });
    setIsDetailsModalOpen(false);
  }, [setIsDetailsModalOpen, queryFilters, setQueryString]);

  const handleAgencyUpdate = useCallback(() => {
    refetchAgencies();
  }, [refetchAgencies]);

  useEffect(() => {
    if (filter.id && !isDetailsModalOpen) {
      setIsDetailsModalOpen(true);
    }
  }, [filter, isDetailsModalOpen]);

  const columns: Array<GridColDef> = useMemo(() => {
    return [
      { field: 'name', headerName: 'Name', flex: 1, sortable: false, display: 'flex' },
      { field: 'city', headerName: 'City', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'identity',
        headerName: 'Identity',
        flex: 1,
        renderCell: (params) => {
          return (
            <>
              {params.row.businessCodeType} - {params.row.businessCodeValue}
            </>
          );
        },
        display: 'flex',
      },
      { field: 'affiliation', headerName: 'Affiliation', flex: 1, sortable: false, display: 'flex' },
      { field: 'tier', headerName: 'Tier', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'action',
        headerName: '',
        flex: 1,
        renderCell: (params) => {
          return (
            <Button variant="text" onClick={() => openDetailsModal(params.row.id)}>
              Show
            </Button>
          );
        },
        display: 'flex',
      },
    ];
  }, [openDetailsModal]);

  return (
    <>
      <Stack display="flex" justifyContent="end">
        <FormControl fullWidth>
          <TextField
            id="search-input-label"
            data-testid="search"
            type="text"
            name="search"
            defaultValue={filter.search}
            label="Search"
            placeholder="Search"
            onChange={(e) => debouncedOnSearchChange(e.target.value)}
            fullWidth
          />
        </FormControl>
      </Stack>
      <Stack mt={1}>
        <DataGrid
          rows={result}
          rowCount={total}
          columns={columns}
          loading={isFetching}
          pagination
          paginationMode="server"
          paginationModel={{ page: filter.page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page }) => onPageChange(page)}
          slots={{ pagination: GridPagination }}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          autoHeight
        />
        <AgencyDetailsModal
          onClose={closeDetailsModal}
          id={filter.id}
          opened={isDetailsModalOpen}
          onUpdate={handleAgencyUpdate}
        />
      </Stack>
    </>
  );
}
